import React, { FC, useEffect } from "react";
import { debounce } from "lodash";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import OrderService from "../OrderService";
import {
  ActiveInactiveButton,
  AuthService,
  ITableHeaders,
  Loader,
  TableHeader,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  OrderStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { OrdersGridModel } from "../../../shared/models"; 
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  TableGrid,
} from "../../../shared/components";
import { GridNames } from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";

type IProps = {
  orderStore: OrderStore;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const OrdersGrid: FC<IProps> = ({
  orderStore,
  navigate,
  itemStore,
  merchantAccountStore,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);
  useEffect(() => {
    loadData(
      orderStore.pagination.pageNumber,
      orderStore.pagination.pageSize,
      "",
      true
    );
    orderStore.searchSubject.subscribe(() => {
      debounceSearch();
    });
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    OrderService.getOrders(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      "createdDate",
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        orderStore.deserializeAPIResponse(response.result || [], response.meta);
      },
    });
  }

  const debounceSearch = debounce(
    () =>
      loadData(
        orderStore.pagination.pageNumber,
        orderStore.pagination.pageSize,
        orderStore.searchValue
      ),
    500
  );

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, orderStore.searchValue);
  }

  function columns() {
    const columns: ITableHeaders<OrdersGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: OrdersGridModel) => {
          const colors = data.getColorsByStatus();
          return (
            <ActiveInactiveButton bgColor={colors.bgColor} color={colors.color}>
              {data.status}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "contact",
        title: "Customer",
        cellColor: "#556bb8",
        cellClassName: "clickable",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: OrdersGridModel) => {
          if (!data.contact) return null;
          return (
            <span
              onClick={() =>
                navigate(`/app/contacts/detail/${data.contact?.id}`)
              }
            >
              {data.contact?.fullName || data.contact?.email}
            </span>
          );
        },
      },
      {
        dataKey: "orderId",
        title: "Order ID",
        className: "table-col-right",
        cellClassName: "table-col-right",

        onCellRender: (data: OrdersGridModel) => {
          return <>{data.orderId}</>;
        },
      },
      {
        dataKey: "oneTime",
        title: "One Time",
        onCellRender: (data: OrdersGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.oneTime);
        },
      },
      {
        dataKey: "recurring",
        title: "Recurring",
        onCellRender: (data: OrdersGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.oneTime);
        },
      },
    ];
    return columns;
  }

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={false}
        showButton={!isNoData}
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function goToDetailPage(id: string) {
    navigate(`/app/orders/detail/${id}`);
  }

  const isNoData =
    firstTimeLoad &&
    orderStore.searchValue === "" &&
    !showLoader &&
    orderStore.pagination.totalItemCount === 0;

  return renderDetails(
    <TableGrid
      tableHeader={
        <TableHeader
          showFilter={orderStore.showFilter}
          onToggleFilter={() => orderStore.toggleFilter()}
          total={orderStore.pagination.totalItemCount}
        />
      }
      totalItemCount={orderStore.pagination.totalItemCount}
      // onRowClick={(data: OrdersGridModel) => goToDetailPage(data.id)}
      cellSpacing={20}
      onPaginationChange={(pageNumber: number, pageSize: number) =>
        onPaginationChange(pageNumber, pageSize)
      }
      pagination={orderStore.pagination}
      data={orderStore.gridData}
      headers={columns()}
      name={GridNames.Order}
      height={
        orderStore.showFilter
          ? "calc(100vh - 499px)"
          : merchantAccountStore.hasNotificationBar()
            ? "calc(100vh - 350px)"
            : ""
      }
      hideTable={isNoData}
      countriesWithState={itemStore.countryStore.countriesWithState}
    />
  );
};

export default withRouter(observer(OrdersGrid));
